/// <amd-module name="Repos/Changes/Components/Diff/Diff" />
import * as React from "react";
import * as Resources from "Repos/Changes/Resources";
import { ChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { DiffViewerType, IFileDiff } from "Repos/ChangesCommon/Contracts";
import { CommentThreadContext } from "Repos/Common/Contracts/Git";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { detectBidiCharacterInArray } from "Repos/Common/Util/BidiDetector";
import { IDiffSummaryDiscussionWidget } from "Repos/Discussion/Common/DiscussionContracts";
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { ScreenContext, ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { onClickFPS } from "VSS/Platform/FPS";
import { VssComponent } from "VSS/Platform/Layout";
import { IIntersectionContext, IntersectionContext } from "VSSUI/Intersection";
import { Link } from "VSSUI/Link";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { Button } from "VSSUI/Button";
import { ChangeSummaryBinaryContent } from "../ChangeSummaryBinaryContent";
import { ChangeSummaryImageContent } from "../ChangeSummaryImageContent";
import { getHiddenCommentCount, IDiffSummaryDiscussionWidgetProvider } from "../Diff/DiffBlock";
import { FileDiscussionWidget } from "../Discussion/FileDiscussionWidget";
import { DiffViewerContext } from "./DiffContext";
import { InlineDiff } from "./InlineDiff";
import { SideBySideDiff } from "./SideBySideDiff";
import { isFeatureEnabled } from "VSS/Platform/Feature";

export interface IDiffProps {
    getCommentPositions?: () => CommentThreadContext[];
    diff: IReadonlyObservableValue<IFileDiff | undefined> | IFileDiff | undefined;
    diffOrientation?: DiffViewerOrientation;
    diffWidth?: ObservableValue<number | undefined>;
    fileUrl?: string | undefined;
    onViewFile?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
    onVisible?: (forceLoad: boolean) => void;
    widgetProvider?: IDiffSummaryDiscussionWidgetProvider;
    loading?: boolean;
}

export interface DiffState {
    displayForce: boolean;
}

export class Diff extends VssComponent<IDiffProps, DiffState> {
    private intersectionContext: IIntersectionContext;
    private observing = false;
    private loadingElement: HTMLDivElement | null;
    private containerRef?: HTMLDivElement;

    constructor(props: IDiffProps) {
        super(props);

        this.state = { displayForce: false };
    }

    public componentDidMount() {
        if (this.intersectionContext) {
            this.intersectionContext.register(this.onIntersect);
        }
        window.addEventListener("resize", this.onResize);
    }

    public componentWillUnmount() {
        if (this.intersectionContext) {
            this.intersectionContext.unregister(this.onIntersect);
        }
        window.removeEventListener("resize", this.onResize);
    }

    public render() {
        const { diff, diffOrientation, getCommentPositions, onVisible, widgetProvider, loading } = this.props;
        return (
            <ScreenContext.Consumer>
                {screen => (
                    <ChangesContext.Consumer>
                        {changesContext => (
                            <Observer
                                diff={diff}
                                orientation={diffOrientation || changesContext.filesService.getDiffOrientation(DiffViewerType.summary)}
                                screenSize={screen.size}
                                widgets={widgetProvider && widgetProvider.getWidgets()}
                            >
                                {(props: {
                                    orientation: DiffViewerOrientation;
                                    diff: IFileDiff | undefined;
                                    screenSize: ScreenSize;
                                    widgets?: IReadonlyObservableArray<IDiffSummaryDiscussionWidget> | IDiffSummaryDiscussionWidget[];
                                }) => {
                                    const commentPositions = getCommentPositions ? getCommentPositions() : undefined;
                                    return (
                                        <DiffViewerContext.Provider value={{ orientation: props.orientation }}>
                                            <FileDiscussionWidget className="margin-left-16 margin-bottom-16" widgets={props.widgets} />
                                            {loading || (onVisible && !props.diff) ? (
                                                <IntersectionContext.Consumer>
                                                    {intersectionContext => {
                                                        this.intersectionContext = intersectionContext;
                                                        return (
                                                            <div
                                                                className="flex-grow flex-column padding-16"
                                                                ref={element => this.observeElement(element)}
                                                            >
                                                                <Spinner />
                                                            </div>
                                                        );
                                                    }}
                                                </IntersectionContext.Consumer>
                                            ) : props.diff ? (
                                                props.diff.imageComparison ? (
                                                    <ChangeSummaryImageContent fileDiff={props.diff} />
                                                ) : props.diff.binaryContent ? (
                                                    <ChangeSummaryBinaryContent fileDiff={props.diff} />
                                                ) : (
                                                    this.renderCodeDiff(
                                                        props.diff,
                                                        commentPositions,
                                                        widgetProvider,
                                                        props.orientation,
                                                        props.screenSize
                                                    )
                                                )
                                            ) : null}
                                        </DiffViewerContext.Provider>
                                    );
                                }}
                            </Observer>
                        )}
                    </ChangesContext.Consumer>
                )}
            </ScreenContext.Consumer>
        );
    }

    private onResize = () => {
        if (this.containerRef && this.props.diffWidth && this.props.diffWidth.value !== this.containerRef.clientWidth) {
            this.props.diffWidth.value = this.containerRef.clientWidth;
        }
    };

    private setContainerRef = (ref: HTMLDivElement) => {
        this.containerRef = ref;
        this.onResize();
    };

    private observeElement = (element: HTMLDivElement | null) => {
        if (element && this.intersectionContext && !this.observing) {
            this.loadingElement = element;
            this.intersectionContext.observe(element);
            this.observing = true;
        }
    };

    private onIntersect = (entries: IntersectionObserverEntry[]) => {
        for (const entry of entries) {
            if (entry.target === this.loadingElement && entry.isIntersecting) {
                this.intersectionContext.unobserve(this.loadingElement);
                this.props.onVisible && this.props.onVisible(false);
            }
        }
    };

    private renderCodeDiff(
        fileDiff: IFileDiff,
        commentPositions: CommentThreadContext[] | undefined,
        widgetProvider: IDiffSummaryDiscussionWidgetProvider | undefined,
        orientation: DiffViewerOrientation,
        screenSize: ScreenSize
    ): JSX.Element {
        const { fileUrl } = this.props;
        let message: JSX.Element | null = null;
        let showDiff = false;

        let { displayForce } = this.state;
        if (displayForce) {
            fileDiff.whitespaceChangesOnly = false;
        }

        if (fileDiff.identicalContent) {
            message = (
                <MessageCard className={"margin-bottom-8"} severity={MessageCardSeverity.Info} messageBarClassName="repos-summary-message">
                    {Resources.ContentIdentical}
                </MessageCard>
            );
        } else if (!displayForce && fileDiff.originalFile && fileDiff.modifiedFile && (fileDiff.originalFileTruncated || fileDiff.modifiedFileTruncated)) {
            const displayButton = isFeatureEnabled(this.context.pageContext, "ms.vss-code-web.large-file-loading-in-pullrequest-summary", false);

            message = (
                <MessageCard className={"margin-bottom-8"} severity={MessageCardSeverity.Warning} messageBarClassName="repos-summary-message">
                    <FormatComponent format={Resources.TooLargeToDiffFormat}>{this.renderFileLink(Resources.NavigateToFile)}</FormatComponent>
                    { displayButton ? (
                        <div className={"margin-left-8"}>
                            <Button 
                                text={Resources.LoadAnyway}
                                onClick={() => {
                                    this.setState({ displayForce: true });
                                    fileDiff.blocks = [];
                                    this.props.onVisible && this.props.onVisible(true);
                                }}
                            />
                        </div>
                    ) : null }
                </MessageCard>
            );
        } else if (fileDiff.whitespaceChangesOnly) {
            message = (
                <MessageCard className={"margin-bottom-8"} severity={MessageCardSeverity.Warning} messageBarClassName="repos-summary-message">
                    {Resources.FileDiffersOnlyInWhitespace}
                </MessageCard>
            );
        } else {
            if (fileDiff.lineCharBlocks.find(lineBlock => detectBidiCharacterInArray(lineBlock.lineChange.mLines))) {
                message = (
                    <MessageCard className={"margin-bottom-8"} severity={MessageCardSeverity.Warning} messageBarClassName="repos-summary-message">
                        <FormatComponent format={Resources.BidiWarningMessage}>
                            <Link href={Resources.BidiDocsLink} target="_blank">{Resources.BidiWarningLinkMessage}</Link>
                        </FormatComponent>
                    </MessageCard>
                );
            }

            showDiff = true;
        }

        const hiddenComments = getHiddenCommentCount(widgetProvider);
        return (
            <div className="repos-summary-code-diff custom-scrollbar" ref={this.setContainerRef}>
                {message}
                {hiddenComments > 0 && fileUrl ? (
                    <MessageCard className={"margin-bottom-8"} severity={MessageCardSeverity.Info} messageBarClassName="repos-summary-message">
                        {this.renderFileLink(hiddenComments > 1 ? format(Resources.OtherComments, hiddenComments) : Resources.OneOtherComment)}
                    </MessageCard>
                ) : null}
                {showDiff ? (
                    orientation === DiffViewerOrientation.Inline || screenSize <= ScreenSize.small ? (
                        <InlineDiff
                            commentPositions={commentPositions}
                            fileDiff={fileDiff}
                            hideOriginalLineNumbers={screenSize <= ScreenSize.small}
                            widgetProvider={widgetProvider}
                        />
                    ) : (
                        <SideBySideDiff commentPositions={commentPositions} fileDiff={fileDiff} widgetProvider={widgetProvider} />
                    )
                ) : null}
            </div>
        );
    }

    private renderFileLink(linkContent: React.ReactNode): JSX.Element {
        const { fileUrl, onViewFile } = this.props;
        return (
            <Link
                href={fileUrl}
                onClick={e => {
                    if (onViewFile && !e.ctrlKey && !e.shiftKey) {
                        onViewFile(e);
                    } else if (fileUrl) {
                        onClickFPS(this.context.pageContext, fileUrl, true, e);
                    }
                }}
            >
                {linkContent}
            </Link>
        );
    }
}
